/**
 * Update User Dialog Component
 * Allows editing of existing user details
 *
 * @module Features/UserManagement/Dialogs
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { useUpdateUserMutation, useGetUserByIdQuery } from '../Service/usersApi';
import {
  updateUserValidationSchema,
  UpdateUserFormData,
  sanitizeUpdateUserData,
} from '../Validation/userValidationSchema';
import { closeUpdateDialog } from '../slices/userManagementSlice';
import { UserRole, UpdateUserRequest } from '../Types';
import { useSnackbar } from 'notistack';

interface UpdateUserDialogProps {
  userId: number;
}

/**
 * Update User Dialog Component
 */
const UpdateUserDialog: React.FC<UpdateUserDialogProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: user, isLoading: isLoadingUser } = useGetUserByIdQuery(userId);
  const [updateUser, { isLoading }] = useUpdateUserMutation();

  // React Hook Form with Yup validation
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateUserFormData>({
    resolver: yupResolver(updateUserValidationSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      roles: ['Clerk'],
      isActive: true,
      mustChangePassword: false,
      unlockAccount: false,
    },
  });

  // Update form when user data is loaded
  React.useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roles: user.roles,
        isActive: user.isActive,
        mustChangePassword: user.mustChangePassword,
        unlockAccount: false,
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: UpdateUserFormData) => {
    try {
      const payload: UpdateUserRequest = sanitizeUpdateUserData(data);

      await updateUser({ id: userId, body: payload }).unwrap();

      enqueueSnackbar('Użytkownik zaktualizowany pomyślnie', { variant: 'success' });
      console.log('✅ User updated successfully');
      handleClose();
    } catch (error: any) {
      const message = error?.data?.message || 'Nie udało się zaktualizować użytkownika';
      enqueueSnackbar(message, { variant: 'error' });
      console.error('❌ Error:', message);
    }
  };

  const handleClose = () => {
    dispatch(closeUpdateDialog());
  };

  if (isLoadingUser) {
    return (
      <Dialog open maxWidth="sm" fullWidth>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 600 }}>Edytuj użytkownika</DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Stack spacing={3}>
          {/* Email field */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoFocus
              />
            )}
          />

          {/* Name fields */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Imię"
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nazwisko"
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Stack>

          {/* Roles selection */}
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.roles}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...field}
                  multiple
                  label="Role"
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value={UserRole.Admin}>{UserRole.Admin}</MenuItem>
                  <MenuItem value={UserRole.Clerk}>{UserRole.Clerk}</MenuItem>
                </Select>
                {errors.roles && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    {errors.roles.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {/* Account status */}
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Status konta
            </Typography>
            <Stack spacing={1}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Konto aktywne"
                  />
                )}
              />
              <Controller
                name="mustChangePassword"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Wymuś zmianę hasła przy następnym logowaniu"
                  />
                )}
              />
              <Controller
                name="unlockAccount"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Odblokuj konto (zresetuj nieudane próby logowania)"
                  />
                )}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Zapisywanie...' : 'Zapisz zmiany'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateUserDialog;
